import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Clock } from "lucide-react";

// ARPS-owned Stripe return page. Payment truth comes from ARPS (status endpoint / webhook),
// NEVER from the browser landing here. Polls until ARPS confirms paid/failed/expired.
export default function StripeReturn({ mode }) {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(mode === "cancel" ? "canceled" : "checking");
  const [info, setInfo] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (mode === "cancel" || !sessionId) return;
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/stripe/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState(data.payment_status); return; }
      } catch (e) { /* keep polling */ }
      tries.current += 1;
      if (tries.current > 20) { setState("timeout"); return; }
      if (!stop) setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, mode]);

  const Card = ({ icon, title, desc, tone }) => (
    <div className="mx-auto mt-16 max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm" data-testid="stripe-return-card">
      <div className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ${tone}`}>{icon}</div>
      <h1 className="mt-4 font-display text-2xl font-bold text-slate-900" data-testid="stripe-return-title">{title}</h1>
      <p className="mt-2 text-sm text-slate-500" data-testid="stripe-return-desc">{desc}</p>
      {info?.invoice_number && (
        <div className="mt-4 rounded-lg border border-slate-100 bg-slate-50 p-3 text-left text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Invoice</span><span className="font-semibold">{info.invoice_number}</span></div>
          {info.job_number && <div className="flex justify-between"><span className="text-slate-500">Job</span><span className="font-semibold">#{info.job_number}</span></div>}
          {info.amount != null && <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="font-semibold">{fmtMoney(info.currency || "USD", info.amount)}</span></div>}
          <div className="flex justify-between"><span className="text-slate-500">Status</span><span className="font-semibold capitalize" data-testid="stripe-return-status">{info.payment_status}</span></div>
        </div>
      )}
      <Button className="mt-6 w-full bg-slate-900 text-white hover:bg-slate-800" data-testid="stripe-return-back" onClick={() => nav("/client/invoices")}>Back to invoices</Button>
    </div>
  );

  if (state === "checking") return <Card tone="bg-slate-100" icon={<Loader2 className="h-7 w-7 animate-spin text-slate-500" />} title="Confirming your payment…" desc="We're verifying your payment with our system. This takes a few seconds." />;
  if (state === "paid") return <Card tone="bg-emerald-100" icon={<CheckCircle2 className="h-7 w-7 text-emerald-600" />} title="Payment confirmed" desc="Your invoice is paid and your order has been released to our process servers." />;
  if (state === "canceled") return <Card tone="bg-amber-100" icon={<Clock className="h-7 w-7 text-amber-600" />} title="Checkout canceled" desc="No payment was taken. You can return and pay your invoice anytime." />;
  if (state === "failed") return <Card tone="bg-red-100" icon={<XCircle className="h-7 w-7 text-red-600" />} title="Payment failed" desc="Your payment did not complete. Please try again to avoid an account hold." />;
  if (state === "expired") return <Card tone="bg-amber-100" icon={<Clock className="h-7 w-7 text-amber-600" />} title="Checkout expired" desc="The checkout session expired before payment. Please start again." />;
  return <Card tone="bg-slate-100" icon={<Clock className="h-7 w-7 text-slate-500" />} title="Still processing" desc="We haven't received confirmation yet. Check your invoices in a moment — if paid, it will update automatically." />;
}
